import {
  Checkbox,
  CheckboxLabel,
  cn,
  FormControl,
  FormControlError,
  FormControlErrorText,
} from '@vidahealth/vida-ui';
import { ComponentProps, type ReactNode } from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { View } from 'react-native';

import { CheckboxCheckIndicator } from '#modules/ui/CheckboxCheckIndicator';

type CheckboxFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  children: ReactNode;
  className?: string;
  checkboxLabelClassName?: string;
  size?: ComponentProps<typeof Checkbox>['size'];
  /**
   * Screen-reader label. Falls back to the RHF field path (`name`) which is
   * a developer identifier ("terms", "consent", "sms") — VoiceOver/TalkBack
   * would read e.g. "terms, checkbox, not checked", which is meaningless to
   * the user. Always pass the user-facing copy on legal-consent checkboxes
   * so SR users hear what they're agreeing to before checking the box.
   */
  accessibilityLabel?: string;
  testID?: string;
};

export function CheckboxField<TFieldValues extends FieldValues>({
  control,
  name,
  children,
  className,
  size = 'md',
  checkboxLabelClassName,
  accessibilityLabel,
  testID,
}: CheckboxFieldProps<TFieldValues>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormControl isInvalid={!!error}>
          <Checkbox
            value={name}
            isChecked={value === true}
            onChange={(next) => onChange(next)}
            className={cn('items-center gap-4', className)}
            aria-label={accessibilityLabel ?? name}
            size={size}
            testID={testID}
          >
            <CheckboxCheckIndicator />
            <View className='flex-1'>
              <CheckboxLabel
                className={cn('font-inter-400 text-typography-700', checkboxLabelClassName)}
              >
                {children}
              </CheckboxLabel>
              {error?.message ? (
                <FormControlError>
                  <FormControlErrorText size='sm'>{error.message}</FormControlErrorText>
                </FormControlError>
              ) : null}
            </View>
          </Checkbox>
        </FormControl>
      )}
    />
  );
}
